/*
global DataDogService
*/
import { clearCache, getAccessToken, getEntitlements } from 'helpers/auth.clientless';
import {
  getAccountCookie,
  anonymousLogin as helperAnonymousLogin,
  isAnonymousAccount,
} from 'helpers/Account';
import { getCookie } from 'helpers/Storage';
import { initAnonymousId } from 'helpers/AnonymousHelper';
import { helperGetApiKey } from 'helpers/ApiHelper';
import { logError, EVENT_TYPE_ANONYMOUS_LOGIN } from 'helpers/LogReportHelpers';
import {
  BUYTHROUGH_DATA_COOKIE_NAME as buythroughDataCookieName,
  MVPD_COOKIE_NAME as mvpdCookieName,
} from 'helpers/AuthHelper';
import {
  getDeviceIdOverride,
  getRequestorId,
  getAuthEndpointConfig,
  getDeviceType,
} from 'helpers/ConfigHelper';
import { isPreviewPass } from 'helpers/PlayerHelper';
import { getSegmentDeviceIdCookie } from '@foxcorp/delta-web-analytics';
import { setUserAuthData } from 'index/AppActions';
import { getMVPDCookie, removeMVPDCookie } from '../index/AuthHelper';

const getDeviceIdFromCookies = (cookies = []) => {
  const deviceIdCookie = cookies.find(cookie => cookie?.deviceId);
  return deviceIdCookie?.deviceId ?? initAnonymousId();
};

const getDefaultClientlessOptions = () => {
  const authEndpointConfig = getAuthEndpointConfig();
  const apiKey = helperGetApiKey();
  const requestor = getRequestorId();
  const deviceType = getDeviceType();

  const options = {
    authEndpointConfig: { ...authEndpointConfig, apiKey },
    deviceType,
    requestor,
    mvpdCookieName,
  };

  return options;
};

export const getStoredAuthenticatedMVPD = account => {
  const mvpd = account?.mvpd;
  const isAuthenticated = mvpd && !isPreviewPass(mvpd);
  return isAuthenticated;
};

const getStoredAuthData = (props = {}) => {
  const initialAccount = getAccountCookie();
  const { currentDeviceId } = props;
  let deviceId = currentDeviceId;
  let segmentDeviceId;
  if (!currentDeviceId) {
    const deviceIdCookie = getSegmentDeviceIdCookie();
    const cookies = [deviceIdCookie, initialAccount];
    segmentDeviceId = getDeviceIdFromCookies(cookies);
    deviceId = getDeviceIdOverride() || segmentDeviceId;
  }

  return {
    deviceId,
    initialAccount,
    segmentDeviceId,
  };
};

export const getUserData = async ({ auth, store, profile, profileBookmarks, profileFavorites }) => {
  let account = null;
  let anonymousAccount = null;
  let isAnonymous;
  let mvpd = null;
  const saveData = data => store.dispatch(setUserAuthData(data));
  const anonymousLogin = async ({ deviceId }) => helperAnonymousLogin(saveData, deviceId, profile);
  const { deviceId, segmentDeviceId } = getStoredAuthData();
  const buythroughScenario = getCookie({ name: buythroughDataCookieName })?.scenario;
  const mvpdCookie = getMVPDCookie();
  let user = profile?.getUserFromCookie();
  if (!user) {
    const error = new Error(EVENT_TYPE_ANONYMOUS_LOGIN);
    logError(error, {
      event: 'debug',
      message: EVENT_TYPE_ANONYMOUS_LOGIN,
      service: DataDogService,
    });
    const { data } = await anonymousLogin({ deviceId });
    const accessToken = getAccessToken(data);
    await auth.setAccessToken({ accessToken });
    await profileBookmarks?.setAccessToken({ accessToken });
    await profileFavorites?.setAccessToken({ accessToken });
    anonymousAccount = data;
    account = null;
    isAnonymous = true;
  } else {
    if (user?.accessTokenExpiration) {
      const refreshedAccount = await profile.refreshToken({ refreshToken: user.refreshToken });
      if (refreshedAccount) {
        user = {
          ...user,
          ...refreshedAccount,
        };
      }
      console.log('Had to refresh account to remove accessTokenExpiration', { refreshedAccount });
    }
    isAnonymous = isAnonymousAccount(user);
    account = isAnonymous ? null : user;
    anonymousAccount = isAnonymous ? user : null;
  }
  const anonymousId = deviceId;
  const latestAuthData = account || anonymousAccount;
  const authorizationToken = getAccessToken(latestAuthData);
  const isAuthenticated = getStoredAuthenticatedMVPD(latestAuthData);
  let preauthorizedResources = null;
  const isMVPDLogin = !!latestAuthData?.mvpd;
  if (isMVPDLogin) {
    mvpd = latestAuthData;
    const entitlements = latestAuthData?.preauthorizedResources;
    const isEntitled = !!entitlements;
    if (isEntitled) preauthorizedResources = entitlements;
  } else if (mvpdCookie) {
    removeMVPDCookie();
  }
  clearCache();
  const getEntitlementOptions = {
    ...getDefaultClientlessOptions(),
    setMVPDToAppState: newMvpd => {
      mvpd = newMvpd;
    },
    deviceId,
    auth,
  };
  const entitlementData = await getEntitlements(getEntitlementOptions);
  const entitlementKeys =
    entitlementData && entitlementData.entitlements
      ? Object.keys(entitlementData.entitlements)
      : [];
  preauthorizedResources = entitlementKeys;
  const accountData = {
    account,
    anonymousAccount,
    anonymousId,
    auth,
    authorizationToken,
    buythroughScenario,
    deviceId,
    isAuthenticated,
    mvpd,
    preauthorizedResources,
    profile,
    profileBookmarks,
    profileFavorites,
    segmentDeviceId,
  };
  saveData(accountData);
  return authorizationToken;
};
